import PropTypes from 'prop-types'
import {
  Body,
  Cell,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table
} from '@table-library/react-table-library/table'
import { Link, generatePath, useSearchParams } from 'react-router-dom'
import {
  FiChevronDown,
  FiChevronUp,
  FiCornerDownRight,
  FiFolder,
  FiMinus
} from 'react-icons/fi'
import { Chip, IconButton, Typography } from '@material-tailwind/react'
import { EditButton } from '@/shared/components/action-buttons'
import { useTranslation } from 'react-i18next'
import { HasAccess } from '@permify/react-role'
import { LinkWithQuery } from '@/shared/components/link-with-query'
import { useState, Fragment } from 'react'
import { useSelector } from 'react-redux'
import { Pagination } from '@/shared/components/pagination'
import { usePagination } from '@table-library/react-table-library/pagination'

const formatDateDMY = (s) => {
  if (!s) return ''
  const [y, m, d] = s.split('-')
  return `${d}-${m}-${y}`
}

const ExpandableCell = ({ expanded = false, buttonProps, cellProps }) => {
  return (
    <Cell {...cellProps}>
      <IconButton {...buttonProps} variant='text' ripple={false}>
        {expanded ? <FiChevronUp size='20px' /> : <FiChevronDown size='20px' />}
      </IconButton>
    </Cell>
  )
}

const contractAdministrators = (item) => {
  const users = item.users?.length ? item.users : [item.user].filter(Boolean)
  return users
}

const AdministratorsCell = ({ item }) => {
  const users = contractAdministrators(item)

  if (users.length === 0) {
    return (
      <Cell>
        <Typography color='gray' variant='small' className='text-gray-500'>
          -
        </Typography>
      </Cell>
    )
  }

  return (
    <Cell>
      <div className='flex flex-col items-start gap-4'>
        {users.map((user) => (
          <div key={user.id} className='flex flex-col items-start'>
            <Typography color='gray' variant='small' className='text-gray-500'>
              {user.name || '-'}
            </Typography>

            <Typography color='gray' className='text-gray-500' variant='small'>
              {user.documentNumber}
            </Typography>

            <Typography variant='small' color='gray' className='text-gray-500'>
              {user.email}
            </Typography>
          </div>
        ))}
      </div>
    </Cell>
  )
}

const ContractRow = ({ item, onExpand, isExpanded = false }) => {
  const { t } = useTranslation()
  const hasChildren = Boolean(item.children.length)
  const { company, offices, activeEmployees, assetsCount } = item

  let sumChildActiveEmployees = 0
  let sumChildAssetsCount = 0
  let sumChildNonconformities = 0

  if (hasChildren && !isExpanded) {
    sumChildActiveEmployees = item.children.reduce((total, child) => total + child.activeEmployees, 0)
    sumChildAssetsCount = item.children.reduce((total, child) => total + child.assetsCount, 0)
    sumChildNonconformities = item.children.reduce((total, child) => total + child.nonconformities, 0)
  }

  return (
    <Row className={isExpanded && '[&>*]:bg-gray-50'} key={item.id} item={item}>
      {hasChildren ? (
        <ExpandableCell
          expanded={isExpanded}
          buttonProps={{ onClick: () => onExpand(item) }}
        />
      ) : (
        <Cell>
          <IconButton variant='text' disabled>
            <FiMinus size='20px' />
          </IconButton>
        </Cell>
      )}
      <Cell>
        {`${item.id} - ${item.name}`}<br />
        {company.name}<br />
        {company.documentNumber}
      </Cell>
      <Cell>
        <p className='text-wrap'>
          {offices.map((office) => office.name).join(', ')}
        </p>
      </Cell>
      <Cell>
          {item.startDate}<br/>
          {item.finishDate}
      </Cell>
      <Cell>{activeEmployees + sumChildActiveEmployees}</Cell>
      <Cell>{assetsCount + sumChildAssetsCount}</Cell>
      <AdministratorsCell item={item} />
      <Cell>
        <Chip
          variant='ghost'
          color={item.status === 'active' ? 'green' : 'red'}
          size='sm'
          className='capitalize text-center h-fit w-fit'
          value={t(item.status + '.one')}
        />
      </Cell>
      <Cell>
        <div className='flex flex-col items-start gap-1'>
          <Chip
            variant='ghost'
            color={item.accredited === 'active' ? 'green' : 'red'}
            size='sm'
            className='capitalize text-center h-fit w-fit'
            value={
              item.accredited === 'active' ? t('common.true') : t('common.false')
            }
          />
          {item.accredited === 'active' && item.accreditationExpiration && (
            <div className='flex flex-col leading-tight'>
              <span className='text-xs text-gray-700'>Vence:</span>
              <span className='text-xs text-gray-700'>
                {formatDateDMY(item.accreditationExpiration)}
              </span>
            </div>
          )}
        </div>
      </Cell>
      <Cell>{item.nonconformities + sumChildNonconformities}</Cell>
      <Cell pinRight>
        <div className='flex gap-3'>
          <HasAccess permissions={['contract#edit']}>
            <Link to={generatePath('/contracts/:id', { id: item.id })}>
              <EditButton />
            </Link>
          </HasAccess>
          <HasAccess permissions='contract#attachments'>
            <LinkWithQuery
              to={generatePath('/contracts/:id/attachments/list', {
                id: item.id
              })}
            >
              <FiFolder className='cursor-pointer' size='1rem' />
            </LinkWithQuery>
          </HasAccess>
        </div>
      </Cell>
    </Row>
  )
}

const ExpandedContractRow = ({ item }) => {
  const { t } = useTranslation()
  const { company, offices, activeEmployees, assetsCount } = item

  return (
    <Row className='[&>*]:bg-gray-50' key={item.id} item={item}>
      <Cell>
        <IconButton variant='text' disabled>
          <FiCornerDownRight size='20px' />
        </IconButton>
      </Cell>
      <Cell>
        {`${item.id} - ${item.name}`}<br />
        {company.name}<br />
        {company.documentNumber}
      </Cell>

      <Cell>
        <p className='text-wrap'>
          {offices.map((office) => office.name).join(', ')}
        </p>
      </Cell>
      <Cell>
          {item.startDate}<br/>
          {item.finishDate}
      </Cell>
      <Cell>{activeEmployees}</Cell>
      <Cell>{assetsCount}</Cell>
      <AdministratorsCell item={item} />
      <Cell>
        <Chip
          variant='ghost'
          color={item.status === 'active' ? 'green' : 'red'}
          size='sm'
          className='capitalize text-center h-fit w-fit'
          value={t(item.status + '.one')}
        />
      </Cell>
      <Cell>
        <div className='flex flex-col items-start gap-1'>
          <Chip
            variant='ghost'
            color={item.accredited === 'active' ? 'green' : 'red'}
            size='sm'
            className='capitalize text-center h-fit w-fit'
            value={
              item.accredited === 'active' ? t('common.true') : t('common.false')
            }
          />
          {item.accredited === 'active' && item.accreditationExpiration && (
            <div className='flex flex-col leading-tight'>
              <span className='text-xs text-gray-700'>Vence:</span>
              <span className='text-xs text-gray-700'>
                {formatDateDMY(item.accreditationExpiration)}
              </span>
            </div>
          )}
        </div>
      </Cell>
      <Cell>{item.nonconformities}</Cell>
      <Cell pinRight>
        <div className='flex gap-3'>
          <HasAccess permissions={['contract#edit']}>
            <Link to={generatePath('/contracts/:id', { id: item.id })}>
              <EditButton />
            </Link>
          </HasAccess>
          <HasAccess permissions='contract#attachments'>
            <LinkWithQuery
              to={generatePath('/contracts/:id/attachments/list', {
                id: item.id
              })}
            >
              <FiFolder className='cursor-pointer' size='1rem' />
            </LinkWithQuery>
          </HasAccess>
        </div>
      </Cell>
    </Row>
  )
}

export const ContractTable = ({ data, theme }) => {
  const { t } = useTranslation()
  const { contracts, meta } = data
  const [searchParams, setSearchParams] = useSearchParams()
  const INITIAL_PAGE = 1
  const perPage = Number(searchParams.get('per_page'))
  const currentPage = Number(searchParams.get('page'))

  const { current: currentUser } = useSelector((state) => state.user)
  const currentRole = currentUser.roles[0] || {}
  // const canSeeExpandableTable = !['contractor'].includes(currentRole.name)
  const mapOnlyParentContracts = (contracts) => {
    return contracts.filter((contract) => !contract.ancestry)
  }

  /** 
  const newData = canSeeExpandableTable
    ? mapOnlyParentContracts(contracts)
    : contracts
  */

  const newData = mapOnlyParentContracts(contracts)

  const nodes = { nodes: newData }

  const [expandedId, setExpandedId] = useState(null)

  const handleExpand = (item) => {
    console.log('item.id', item)
    if (expandedId === item.id) {
      setExpandedId(null)
      return
    }

    setExpandedId(item.id)
  }

  const onPaginationChange = (_, state) => {
    const page = state.page + 1 || INITIAL_PAGE

    searchParams.set('page', page)
    setSearchParams(searchParams)
  }

  const pagination = usePagination(
    nodes,
    {
      state: {
        page: currentPage - 1 || 0,
        size: perPage
      },
      onChange: onPaginationChange
    },
    {
      isServer: true
    }
  )

  return (
    <div className='relative flex flex-col justify-between gap-4 h-full'>
      <div>
        <Table
          data={nodes}
          pagination={pagination}
          layout={{ fixedHeader: true, custom: true }}
          theme={theme}
          className='h-max z-0'
        >
          {(tableList) => (
            <>
              <Header>
                <HeaderRow>
                  <HeaderCell resize></HeaderCell>
                  <HeaderCell resize>{t('contract.one')}</HeaderCell>
                  <HeaderCell resize>{t('office.one')}</HeaderCell>
                  <HeaderCell resize>Inicio - Término</HeaderCell>
                  <HeaderCell resize>{`N° ${t('employee.other')}`}</HeaderCell>
                  <HeaderCell resize>{`N° ${t('asset.other')}`}</HeaderCell>
                  <HeaderCell resize>{t('admin.one')}</HeaderCell>
                  <HeaderCell resize>{t('status.one')}</HeaderCell>
                  <HeaderCell>Acreditado</HeaderCell>
                  <HeaderCell>NC</HeaderCell>
                  <HeaderCell pinRight resize>
                    {t('actions.other')}
                  </HeaderCell>
                </HeaderRow>
              </Header>

              <Body>
                {tableList.map((item) => (
                  <Fragment key={item.id}>
                    <ContractRow
                      onExpand={handleExpand}
                      item={item}
                      isExpanded={expandedId === item.id}
                    />
                    {expandedId === item.id &&
                      Boolean(item.children.length) &&
                      item.children.map((contractChild) => (
                        <ExpandedContractRow
                          key={item.id}
                          item={contractChild}
                        />
                      ))}
                  </Fragment>
                ))}
              </Body>
            </>
          )}
        </Table>
      </div>
      <Pagination
        onPageChange={({ selected }) => pagination.fns.onSetPage(selected)}
        page={pagination.state.page}
        totalPage={meta.pagination?.total_pages}
      />
    </div>
  )
}

ContractTable.propTypes = {
  data: PropTypes.shape({
    contracts: PropTypes.array,
    meta: PropTypes.object
  })
}
